import React, { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../Config/Config";
import Header from "../layout/Header";

// ─── Font loader ─────────────────────────────────────────────────
const _link = document.createElement("link");
_link.rel = "stylesheet";
_link.href =
  "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap";
document.head.appendChild(_link);

// ─── Tokens ──────────────────────────────────────────────────────
const C = {
  bg: "#F1F5F9",
  surface: "#FFFFFF",
  navy: "#0F2137",
  navyMid: "#1e3a5f",
  teal: "#0D9488",
  tealLight: "#CCFBF1",
  amber: "#D97706",
  amberLight: "#FEF3C7",
  slate: "#475569",
  muted: "#94A3B8",
  border: "#E2E8F0",
  divider: "#F1F5F9",
};

const FONT = {
  ui: "'DM Sans', system-ui, sans-serif",
  mono: "'DM Mono', 'Courier New', monospace",
};

// ─── Global styles ────────────────────────────────────────────────
const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.bg}; font-family: ${FONT.ui}; }

  .cs-banner {
    background: linear-gradient(160deg, ${C.navy} 0%, ${C.navyMid} 100%);
    padding: 60px 24px 52px;
    text-align: center;
    position: relative;
    overflow: hidden;
  }
  .cs-banner::after {
    content: '';
    position: absolute;
    bottom: -1px; left: 0; right: 0;
    height: 40px;
    background: ${C.bg};
    clip-path: ellipse(55% 100% at 50% 100%);
  }

  .cs-search-wrap { position: relative; }
  .cs-search {
    width: 100%;
    padding: 13px 20px 13px 46px;
    border: 2px solid ${C.border};
    border-radius: 10px;
    font-family: ${FONT.ui};
    font-size: 15px;
    background: ${C.surface};
    color: ${C.navy};
    outline: none;
    transition: border-color .18s;
  }
  .cs-search:focus { border-color: ${C.teal}; }
  .cs-search-icon {
    position: absolute;
    left: 15px; top: 50%;
    transform: translateY(-50%);
    color: ${C.muted};
    pointer-events: none;
    font-size: 16px;
  }

  .cs-dept-label {
    font-family: ${FONT.ui};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    color: ${C.muted};
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .cs-dept-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${C.border};
  }

  .cs-card {
    background: ${C.surface};
    border-radius: 12px;
    border: 1px solid ${C.border};
    overflow: hidden;
    transition: box-shadow .2s, transform .2s;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .cs-card:hover {
    box-shadow: 0 8px 28px rgba(15,33,55,.1);
    transform: translateY(-3px);
  }

  .cs-card-month {
    padding: 16px 18px 14px;
    border-bottom: 1px solid ${C.divider};
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .cs-stat-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    background: ${C.border};
    border-bottom: 1px solid ${C.border};
  }
  .cs-stat-cell {
    background: ${C.surface};
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .cs-stat-num {
    font-family: ${FONT.mono};
    font-size: 24px;
    font-weight: 500;
    letter-spacing: -1px;
    line-height: 1;
  }
  .cs-stat-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: ${C.muted};
  }

  .cs-procedures {
    padding: 14px 18px;
    flex: 1;
  }
  .cs-procedures-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
    border: none;
    background: none;
    width: 100%;
    padding: 0;
    font-family: ${FONT.ui};
  }
  .cs-procedures-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: ${C.muted};
  }
  .cs-procedures-count {
    font-size: 11px;
    font-weight: 600;
    background: ${C.divider};
    color: ${C.slate};
    padding: 2px 8px;
    border-radius: 20px;
  }
  .cs-procedures-body {
    margin-top: 10px;
    font-size: 13.5px;
    color: ${C.slate};
    line-height: 1.6;
    white-space: pre-line;
    max-height: 140px;
    overflow-y: auto;
    padding-right: 4px;
  }
  .cs-procedures-body::-webkit-scrollbar { width: 4px; }
  .cs-procedures-body::-webkit-scrollbar-track { background: transparent; }
  .cs-procedures-body::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 4px; }

  .cs-bar {
    height: 3px;
    border-radius: 0;
  }

  .cs-empty {
    text-align: center;
    padding: 80px 20px;
    color: ${C.muted};
  }

  @media (max-width: 600px) {
    .cs-stat-num { font-size: 20px; }
  }
`;

// ─── Helpers ─────────────────────────────────────────────────────
function formatMonth(str) {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
  } catch {
    return str;
  }
}

function countLines(str) {
  if (!str) return 0;
  return str.split("\n").filter((l) => l.trim()).length;
}

// ─── Card ─────────────────────────────────────────────────────────
function StatCard({ item }) {
  const [open, setOpen] = useState(false);
  const opd = parseInt(item.TotalOPD || 0);
  const ipd = parseInt(item.TotalIPD || 0);
  const procCount = countLines(item.MajorProcedures);

  return (
    <div className="cs-card" style={{ marginTop: "2rem" }}>
      {/* OPD pulse bar — teal width proportional to OPD (visual rhythm) */}
      <div
        className="cs-bar"
        style={{
          background: `linear-gradient(90deg, ${C.teal} 0%, ${C.tealLight} 100%)`,
        }}
      />

      {/* Month header */}
      <div className="cs-card-month">
        <span style={{ fontWeight: 700, fontSize: "15px", color: C.navy }}>
          {formatMonth(item.Month)}
        </span>
        <span
          style={{
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "1px",
            textTransform: "uppercase",
            color: C.muted,
          }}
        >
          {item.Category || ""}
        </span>
      </div>

      {/* OPD / IPD stats */}
      <div className="cs-stat-row">
        <div className="cs-stat-cell">
          <span className="cs-stat-num" style={{ color: C.teal }}>
            {opd.toLocaleString()}
          </span>
          <span className="cs-stat-label">OPD</span>
        </div>
        <div className="cs-stat-cell">
          <span className="cs-stat-num" style={{ color: C.amber }}>
            {ipd.toLocaleString()}
          </span>
          <span className="cs-stat-label">IPD</span>
        </div>
      </div>

      {/* Procedures */}
      <div className="cs-procedures">
        <button
          className="cs-procedures-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="cs-procedures-label">Major Procedures</span>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {procCount > 0 && (
              <span className="cs-procedures-count">{procCount}</span>
            )}
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              style={{
                color: C.muted,
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform .18s",
              }}
            >
              <path
                d="M2 5l5 5 5-5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </button>

        {open && (
          <div className="cs-procedures-body">
            {item.MajorProcedures?.trim() || (
              <span style={{ color: C.muted, fontStyle: "italic" }}>
                None recorded
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────
export default function Index() {
  const [groupedData, setGroupedData] = useState({});
  const [search, setSearch] = useState("");

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "NDCS", "Monthly Clinical Statistics"),
      (docSnap) => {
        if (docSnap.exists()) {
          const tempData = Object.entries(docSnap.data()).map(
            ([key, value]) => ({
              Id: key,
              ...value,
            }),
          );

          const grouped = {};
          tempData.forEach((item) => {
            const dept = item.Category || "Others";
            if (!grouped[dept]) grouped[dept] = [];
            grouped[dept].push(item);
          });

          Object.keys(grouped).forEach((dept) => {
            grouped[dept].sort((a, b) => b.Month.localeCompare(a.Month));
          });

          setGroupedData(grouped);
        } else {
          setGroupedData({});
        }
      },
    );
    return () => unsubscribe();
  }, []);

  const filteredGroups = Object.keys(groupedData).filter((dept) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      dept.toLowerCase().includes(q) ||
      groupedData[dept].some((item) =>
        JSON.stringify(item).toLowerCase().includes(q),
      )
    );
  });

  // Summary totals
  const allRecords = Object.values(groupedData).flat();
  const grandOPD = allRecords.reduce(
    (s, i) => s + parseInt(i.TotalOPD || 0),
    0,
  );
  const grandIPD = allRecords.reduce(
    (s, i) => s + parseInt(i.TotalIPD || 0),
    0,
  );

  return (
    <div style={{ fontFamily: FONT.ui, background: C.bg, minHeight: "100vh" }}>
      <style>{CSS}</style>
      <Header />

      {/* Banner */}
      <div className="cs-banner">
        <p
          style={{
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            color: C.teal,
            marginBottom: "10px",
            marginTop: "4rem",
          }}
        >
          NDC · Clinical Records
        </p>
        <h1
          style={{
            fontFamily: FONT.ui,
            fontSize: "clamp(26px, 5vw, 44px)",
            fontWeight: 700,
            color: "#fff",
            marginBottom: "10px",
            letterSpacing: "-0.5px",
          }}
        >
          Monthly Clinical Statistics
        </h1>
        <p style={{ color: "rgba(255,255,255,.55)", fontSize: "15px" }}>
          Department-wise OPD, IPD &amp; Major Procedures
        </p>

        {/* Grand totals */}
        <div
          style={{
            display: "inline-flex",
            gap: "0",
            marginTop: "28px",
            background: "rgba(255,255,255,.08)",
            border: "1px solid rgba(255,255,255,.12)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "14px 28px",
              borderRight: "1px solid rgba(255,255,255,.12)",
            }}
          >
            <div
              style={{
                fontFamily: FONT.mono,
                fontSize: "22px",
                fontWeight: 500,
                color: "#5EEAD4",
                letterSpacing: "-1px",
              }}
            >
              {grandOPD.toLocaleString()}
            </div>
            <div
              style={{
                fontSize: "11px",
                letterSpacing: "1.2px",
                textTransform: "uppercase",
                color: "rgba(255,255,255,.45)",
                marginTop: "2px",
              }}
            >
              Total OPD
            </div>
          </div>
          <div style={{ padding: "14px 28px" }}>
            <div
              style={{
                fontFamily: FONT.mono,
                fontSize: "22px",
                fontWeight: 500,
                color: "#FCD34D",
                letterSpacing: "-1px",
              }}
            >
              {grandIPD.toLocaleString()}
            </div>
            <div
              style={{
                fontSize: "11px",
                letterSpacing: "1.2px",
                textTransform: "uppercase",
                color: "rgba(255,255,255,.45)",
                marginTop: "2px",
              }}
            >
              Total IPD
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "36px 24px 80px",
        }}
      >
        {/* Search */}
        <div style={{ maxWidth: "480px", marginBottom: "40px" }}>
          <div className="cs-search-wrap">
            <span className="cs-search-icon">🔍</span>
            <input
              className="cs-search"
              type="text"
              placeholder="Search department or procedure…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {filteredGroups.length === 0 ? (
          <div className="cs-empty">
            <div style={{ fontSize: "36px", marginBottom: "12px" }}>🔍</div>
            <p style={{ fontSize: "16px", fontWeight: 600, color: C.slate }}>
              No records found
            </p>
            <p style={{ fontSize: "14px", marginTop: "6px" }}>
              Try a different department name or month
            </p>
          </div>
        ) : (
          filteredGroups.map((department) => {
            const records = groupedData[department];
            const deptOPD = records.reduce(
              (s, i) => s + parseInt(i.TotalOPD || 0),
              0,
            );
            const deptIPD = records.reduce(
              (s, i) => s + parseInt(i.TotalIPD || 0),
              0,
            );

            return (
              <div key={department} style={{ marginBottom: "52px" }}>
                {/* Dept header */}
                <div style={{ marginBottom: "20px" }}>
                  <div className="cs-dept-label">{department}</div>
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <h2
                      style={{
                        fontSize: "clamp(18px,3vw,24px)",
                        fontWeight: 700,
                        color: C.navy,
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {department}
                    </h2>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <span
                        style={{
                          background: C.tealLight,
                          color: C.teal,
                          fontSize: "12px",
                          fontWeight: 600,
                          padding: "3px 10px",
                          borderRadius: "20px",
                          fontFamily: FONT.mono,
                        }}
                      >
                        OPD {deptOPD.toLocaleString()}
                      </span>
                      <span
                        style={{
                          background: C.amberLight,
                          color: C.amber,
                          fontSize: "12px",
                          fontWeight: 600,
                          padding: "3px 10px",
                          borderRadius: "20px",
                          fontFamily: FONT.mono,
                        }}
                      >
                        IPD {deptIPD.toLocaleString()}
                      </span>
                      <span
                        style={{
                          background: C.divider,
                          color: C.slate,
                          fontSize: "12px",
                          fontWeight: 600,
                          padding: "3px 10px",
                          borderRadius: "20px",
                        }}
                      >
                        {records.length} month{records.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cards grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {records.map((item) => (
                    <StatCard key={item.Id} item={item} />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
